import { EChartsOption } from "echarts";
import { arrayOrEmpty } from "utils";

import { FeatureImportanceSummary } from "../types";
import { FALLBACK_COLOR, NOT_AVAILABLE } from "./constants";
import { EChartsTransformer } from "./transformer";

function roundUpToNice(value: number) {
  if (value <= 10) return 10;
  const exponent = Math.floor(Math.log10(value));
  const magnitude = Math.pow(10, exponent);
  return Math.ceil(value / magnitude) * magnitude;
}
export const predictedVsActualTransformer: EChartsTransformer<FeatureImportanceSummary> =
  {
    transform: (fiSummary, wellData, _, __, target) => {
      if (!fiSummary || !wellData) {
        return {};
      }
      const predicted = arrayOrEmpty(fiSummary.summary.predicted);
      const actuals = fiSummary.summary.targets;
      const entities = fiSummary.summary.entities;
      const targetUnit = target?.Unit || "";
      const targetUnitLabel = targetUnit ? ` (${targetUnit})` : "";
      // Group data by group name
      const groupMap: Record<
        string,
        { color: string; data: { value: (number | string)[] }[] }
      > = {};
      predicted.forEach((predictedValue, index) => {
        const entity = entities[index];
        const color = wellData[entity]?.color || FALLBACK_COLOR;
        const group = wellData[entity]?.title || NOT_AVAILABLE;
        if (!groupMap[group]) {
          groupMap[group] = { color, data: [] };
        }
        groupMap[group].data.push({
          value: [actuals[index], predictedValue, entity, group]
        });
      });

      const series = Object.entries(groupMap).map(([group, { color, data }]) => ({
        name: group,
        type: "scatter",
        data,
        largeThreshold: 2000,
        symbolSize: 8,
        large: true,
        itemStyle: { color }
      }));
      const allValues = [...actuals, ...predicted];
      const maxValue = roundUpToNice(Math.max(...allValues));
      return {
        animation: false,
        tooltip: {
          trigger: "item",
          formatter: function (params) {
            if (!params) {
              return;
            }
            return `<strong>${params.data.value[2]}</strong>
            <br/>Group: ${params.data.value[3]}
            <br/>Actual${targetUnitLabel}: ${params.data.value[0].toFixed(2)}
            <br/>Predicted${targetUnitLabel}: ${params.data.value[1].toFixed(2)}`;
          }
        },
        grid: {
          top: 10,
          bottom: 25,
          left: 50,
          right: 25,
          containLabel: true
        },
        dataZoom: [
          { type: "inside", xAxisIndex: 0, filterMode: "none" },
          { type: "inside", yAxisIndex: 0, filterMode: "none" }
        ],
        xAxis: {
          type: "value",
          name: `Actual Value${targetUnitLabel}`,
          nameLocation: "middle",
          nameGap: 30,
          max: maxValue
        },
        yAxis: {
          type: "value",
          name: `Predicted Value${targetUnitLabel}`,
          nameLocation: "middle",
          nameGap: 60,
          max: maxValue
        },
        series: [
          ...series,
          {
            name: "y = x",
            type: "line",
            showSymbol: false,
            data: [
              [0, 0],
              [Math.max(...predicted, ...actuals), Math.max(...predicted, ...actuals)]
            ],
            lineStyle: {
              width: 2,
              type: "dashed",
              color: "rgba(0, 0, 0, 0.5)"
            }
          }
        ]
      } as EChartsOption;
    }
  };
